import { useState, useEffect, useRef } from "react";
import { t, font, Button } from "@realtyflows/ui";
import {
  getFolderUploads,
  uploadFileToFolder,
  updateFolderUpload,
  getFolderUploadDownloadUrl,
  deleteFolderUpload,
} from "../api";

/**
 * FolderUploads — V1 Section B
 *
 * Mounted inside FolderDetail. Renders the uploads section of a folder:
 *   • drag-drop zone (also fires on click → file picker)
 *   • list of existing uploads with label, size, include-in-envelope toggle
 *   • download / remove actions
 *
 * Uploads use the signed-URL flow: request URL → PUT to GCS → confirm.
 *
 * The `include_in_envelope` flag lets the user mark a PDF for inclusion
 * when the whole folder is sent for signing (V1 Section B feature 1.14).
 */
export default function FolderUploads({ folderId }) {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const refresh = () => {
    if (!folderId) return;
    setLoading(true);
    getFolderUploads(folderId)
      .then(setUploads)
      .catch(() => setUploads([]))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [folderId]);

  const onFiles = async (fileList) => {
    setError("");
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      // Upload sequentially so we surface errors clearly per file.
      for (const file of files) {
        await uploadFileToFolder(folderId, file);
      }
      refresh();
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    onFiles(e.dataTransfer.files);
  };

  const onDownload = async (uploadId) => {
    try {
      const { url } = await getFolderUploadDownloadUrl(folderId, uploadId);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      setError(err.message || "Could not generate download URL");
    }
  };

  const onToggleEnvelope = async (upload) => {
    try {
      const updated = await updateFolderUpload(folderId, upload.id, {
        includeInEnvelope: !upload.include_in_envelope,
      });
      setUploads((curr) => curr.map((u) => (u.id === upload.id ? { ...u, ...updated } : u)));
    } catch (err) {
      setError(err.message || "Could not update upload");
    }
  };

  const onLabelChange = async (upload, label) => {
    try {
      const updated = await updateFolderUpload(folderId, upload.id, { label });
      setUploads((curr) => curr.map((u) => (u.id === upload.id ? { ...u, ...updated } : u)));
    } catch (err) {
      setError(err.message || "Could not update label");
    }
  };

  const onRemove = async (upload) => {
    if (!window.confirm(`Remove "${upload.file_name}" from this folder?`)) return;
    try {
      await deleteFolderUpload(folderId, upload.id);
      setUploads((curr) => curr.filter((u) => u.id !== upload.id));
    } catch (err) {
      setError(err.message || "Could not remove upload");
    }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div>
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? t.emerald : t.border}`,
          borderRadius: 10,
          padding: 20,
          textAlign: "center",
          cursor: uploading ? "wait" : "pointer",
          background: dragOver ? t.emerald + "08" : t.surfaceWarm,
          transition: "all 0.15s ease",
          marginBottom: 14,
        }}
      >
        <div style={{ fontSize: 13, color: t.textPrimary, fontWeight: 600, fontFamily: font.body }}>
          {uploading ? "Uploading..." : "Drop files here or click to browse"}
        </div>
        <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>
          PDF, DOCX, JPG, PNG · max 25 MB each
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic"
          style={{ display: "none" }}
          onChange={(e) => onFiles(e.target.files)}
        />
      </div>

      {error && (
        <div style={{
          background: t.coral + "15", border: `1px solid ${t.coral}40`,
          color: t.coral, padding: "8px 12px", borderRadius: 6, fontSize: 12,
          marginBottom: 12, fontFamily: font.body,
        }}>
          {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ fontSize: 12, color: t.textMuted, padding: "10px 0", fontFamily: font.body }}>Loading...</div>
      ) : uploads.length === 0 ? (
        <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", padding: "10px 0", fontFamily: font.body }}>
          No uploads yet. Drop a PDF or scan above to attach it to this folder.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {uploads.map((u) => (
            <div key={u.id} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: 10, background: t.surface,
              border: `1px solid ${t.border}`, borderRadius: 8,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 6,
                background: t.surfaceWarm, display: "flex",
                alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, color: t.textMuted,
                fontFamily: font.mono, flexShrink: 0,
              }}>
                {(u.file_name?.split(".").pop() || "").slice(0, 4).toUpperCase()}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: t.textPrimary,
                  fontFamily: font.body, overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {u.file_name}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                  <input
                    type="text"
                    placeholder="Add label (e.g. Pre-Approval)"
                    defaultValue={u.label || ""}
                    onBlur={(e) => {
                      if (e.target.value !== (u.label || "")) onLabelChange(u, e.target.value);
                    }}
                    style={{
                      flex: 1, fontSize: 11, fontFamily: font.body,
                      padding: "3px 6px", borderRadius: 4,
                      border: `1px solid ${t.border}`, background: t.surfaceWarm,
                      color: t.textPrimary, outline: "none", maxWidth: 200,
                    }}
                  />
                  <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono }}>
                    {fmtSize(u.file_size_bytes)}
                  </span>
                </div>
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: t.textMuted, fontFamily: font.body, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={!!u.include_in_envelope}
                  onChange={() => onToggleEnvelope(u)}
                />
                Include in envelope
              </label>

              <Button variant="secondary" size="sm" onClick={() => onDownload(u.id)}>Download</Button>
              <button
                onClick={() => onRemove(u)}
                aria-label="Remove upload"
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  color: t.coral, fontSize: 16, fontWeight: 700, padding: "0 6px",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
